import { Link } from "react-router-dom";

/**
 * Inter is loaded in index.html — use it here for a clean sans-serif CTA,
 * matching the reference (weights + tracking).
 */
export default function CtaBanner() {
  return (
    <section className="bg-white px-4 py-8 sm:px-6 lg:py-10">
      <div className="mx-auto w-full max-w-4xl font-sans">
        <div className="overflow-hidden rounded-2xl border border-white/10 bg-[linear-gradient(135deg,#1a1a1a,#252525)] shadow-2xl shadow-black/25 ring-1 ring-white/5 sm:rounded-3xl">
          <div className="grid gap-8 px-6 py-10 sm:px-8 sm:py-11 md:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] md:gap-0 md:py-12 lg:px-10">
            {/* Left — headline, copy, actions */}
            <div className="md:pr-8">
              <h2 className="text-base font-extrabold uppercase tracking-[0.12em] text-white sm:text-lg">
                Ready to discuss your project?
              </h2>
              <p className="mt-4 text-[15px] font-normal leading-relaxed text-[#a3a3a3]">
                Whether you need traffic controllers for tomorrow or a full
                traffic management plan for a major project, we&apos;re here to
                help. Get in touch for a no-obligation quote.
              </p>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:gap-3">
                <a
                  href="tel:1300118170"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-orange-500 px-5 py-3 text-sm font-semibold text-white transition hover:bg-orange-600 active:scale-[0.98] sm:shrink-0"
                >
                  <svg
                    className="h-5 w-5 shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                  CALL 1300 118 170
                </a>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-neutral-500 bg-[#2c2c2c] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#363636] sm:shrink-0"
                >
                  <svg
                    className="h-5 w-5 shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  EMAIL US
                </Link>
              </div>
            </div>

            {/* Right — feature cards */}
            <div className="flex flex-col justify-center gap-4 border-t border-white/10 pt-8 md:border-l md:border-t-0 md:pl-8 md:pt-0">
              <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                <div className="flex gap-4">
                  <div
                    className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-[#3d2a1f] text-orange-500 ring-1 ring-orange-500/25"
                    aria-hidden
                  >
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.75}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div className="min-w-0 pt-0.5">
                    <p className="text-[15px] font-bold text-white">
                      Quick Response
                    </p>
                    <p className="mt-1.5 text-sm font-normal leading-snug text-[#a3a3a3]">
                      We respond to all enquiries within 24 hours.
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                <div className="flex gap-4">
                  <div
                    className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-[#3d2a1f] text-orange-500 ring-1 ring-orange-500/25"
                    aria-hidden
                  >
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.75}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.75}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  </div>
                  <div className="min-w-0 pt-0.5">
                    <p className="text-[15px] font-bold text-white">
                      NSW Wide
                    </p>
                    <p className="mt-1.5 text-sm font-normal leading-snug text-[#a3a3a3]">
                      We service all areas across New South Wales.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
